/*
 * component-cart-main.js - <cart-main> Custom Element for the full cart page.
 *
 * Mirrors the drawer's delegated handlers so cart-page mutations + promo applies
 * route through cart-store. The drawer (component-cart-drawer.js) owns the drawer.
 * This CE owns the /cart page body. Both are wired to the same event contracts.
 *
 * Responsibilities:
 *   - Delegate qty-stepper:change to cartStore.change(line.key, qty).
 *   - Delegate [data-cart-remove] click to cartStore.change(line.key, 0).
 *   - Delegate [data-cart-discount-remove] click to cartStore.removeDiscount().
 *   - Delegate promo form submit to cartStore.applyDiscount() with bundle-active guard.
 *   - Render inline promo error messages on cart:error { op: 'discount' }.
 *
 * Re-binds on SRA swap via connectedCallback.
 */

class CartMain extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onSubmit = this._onSubmit.bind(this);
    this._onError = this._onError.bind(this);
  }

  connectedCallback() {
    document.addEventListener('cart:error', this._onError);
    this.addEventListener('click', this._onClick);
    this.addEventListener('qty-stepper:change', this._onChange);
    this.addEventListener('submit', this._onSubmit);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:error', this._onError);
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('qty-stepper:change', this._onChange);
    this.removeEventListener('submit', this._onSubmit);
  }

  _onClick(e) {
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      const line = remove.closest('[data-line-key]');
      if (line && window.cartStore) {
        window.cartStore.change(line.dataset.lineKey, 0).catch(() => {});
      }
      return;
    }
    const removeDiscount = e.target.closest('[data-cart-discount-remove]');
    if (removeDiscount && window.cartStore) {
      window.cartStore.removeDiscount().catch(() => {});
      return;
    }
  }

  _onChange(e) {
    const stepper = e.target.closest('qty-stepper');
    if (!stepper) return;
    const line = stepper.closest('[data-line-key]');
    if (!line || !window.cartStore) return;
    const detail = e.detail || {};
    const qty = Math.max(1, Number(detail.value) || 1);
    window.cartStore.change(line.dataset.lineKey, qty).catch(() => {});
  }

  _onSubmit(e) {
    const form = e.target.closest('[data-promo-form]');
    if (!form) return;
    e.preventDefault();
    const messageRegion = form.parentNode && form.parentNode.querySelector('[data-message-region]');
    const input = form.querySelector('input[name="discount"]');
    if (messageRegion) messageRegion.textContent = '';
    if (input) input.setAttribute('aria-invalid', 'false');

    const code = (input && input.value || '').trim();
    if (!code) return;

    const host = form.closest('cart-discount');
    const bundleActive = host && host.dataset.bundleActive === 'true';
    if (bundleActive) {
      this._renderPromoError(form, 'bundle_active');
      this._announceAlert(this._messageForCode('bundle_active'));
      return;
    }

    if (window.cartStore) {
      const applyBtn = form.querySelector('.cart-discount__apply');
      if (applyBtn) { applyBtn.disabled = true; applyBtn.classList.add('is-loading'); }
      window.cartStore.applyDiscount(code)
        .catch(() => {})
        .finally(() => {
          if (applyBtn) { applyBtn.disabled = false; applyBtn.classList.remove('is-loading'); }
        });
    }
  }

  _onError(e) {
    const detail = e && e.detail;
    if (!detail || detail.op !== 'discount') return;
    const forms = document.querySelectorAll('[data-promo-form]');
    forms.forEach((form) => this._renderPromoError(form, detail.code));
  }

  _renderPromoError(form, code) {
    const message = this._messageForCode(code);
    const region = form.parentNode && form.parentNode.querySelector('[data-message-region]');
    const input = form.querySelector('input[name="discount"]');
    if (region) region.textContent = message;
    if (input) input.setAttribute('aria-invalid', 'true');
    // Force the disclosure open so the message is visible. The SRA-rendered
    // HTML replaces the <details> without preserving its open state; see
    // component-cart-drawer.js for the full rationale.
    const details = form.closest('details');
    if (details) details.open = true;
  }

  _messageForCode(code) {
    const messages = window.LunchedCartMessages || {};
    const raw = messages[code] || messages.generic || 'That code could not be applied.';
    return this._decodeHtmlEntities(raw);
  }

  _decodeHtmlEntities(s) {
    // Shopify Liquid's `| t` filter HTML-escapes apostrophes/quotes/etc before
    // `| json` wraps the string for JS. textContent rendering would then show
    // literal `&#39;` instead of `'`. textarea-innerHTML decode is the safe
    // way to undo this (plaintext context, no tag parsing).
    if (typeof s !== 'string') return s;
    if (s.indexOf('&') === -1) return s;
    const txt = document.createElement('textarea');
    txt.innerHTML = s;
    return txt.value;
  }

  _announceAlert(text) {
    const region = document.getElementById('cart-live-alert');
    if (!region) return;
    region.textContent = '';
    setTimeout(() => { region.textContent = 'Error: ' + text; }, 50);
  }
}

if (!customElements.get('cart-main')) {
  customElements.define('cart-main', CartMain);
}
